import axios from "axios";
import { ref, computed } from "vue";
import { defineStore } from "pinia";
import eventBus from "Admin/js/Utilities/EventBus.js";
import Utilities from "Admin/js/Helpers/utilities.js";
import { useUserStore } from "./User";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation.js";

export const useRolesAndPermissions = defineStore("rolesAndPermissions", () => {
    const user_store = useUserStore();
    const { isLoading, manageAsyncOperation } = useAsyncOperation();

    const PERMISSIONS = {
        INVITER_ADMIN: "inviter-admin",
        WORKDAY_START: "workday-start",
        WORKDAY_STOP: "workday-stop",
    };

    const ROLES = {
        OWNER: "owner",
        ADMIN: "admin",
        SUPPORT_ADMIN: "support_admin",
    };

    const WORKDAYS = {
        SUNDAY: 0,
        MONDAY: 1,
        TUESDAY: 2,
        WEDNESDAY: 3,
        THURSDAY: 4,
        FRIDAY: 5,
        SATURDAY: 6,
    };

    const timezone = ref(null);
    const timezoneOptions = ref([]);
    const canStartWorkdayPermission = ref({});
    const canStopWorkdayPermission = ref({});
    const roles = ref([]);
    const isWorkdayStartPermission = ref(false);
    const isWorkdayStopPermission = ref(false);
    const inviterAdminPermission = ref({});
    const officeHours = ref([]);

    const hasWorkdayStartPermission = computed(() => {
        return user_store.isAuthorizedFor(PERMISSIONS.WORKDAY_START);
    });

    const hasWorkdayStopPermission = computed(() => {
        return user_store.isAuthorizedFor(PERMISSIONS.WORKDAY_STOP);
    });

    const getOwners = computed(() => {
        return (
            roles.value.find((role) => role.name === ROLES.OWNER)
                ?.user_profile_ids ?? []
        );
    });

    const getAdmins = computed(() => {
        const admin_role = roles.value.find(
            (role) => role.name === ROLES.ADMIN,
        );
        const support_admin_role = roles.value.find(
            (role) => role.name === ROLES.SUPPORT_ADMIN,
        );

        const admin_user_profile_ids = admin_role?.user_profile_ids ?? [];
        const support_admin_user_profile_ids =
            support_admin_role?.user_profile_ids ?? [];

        const profilesIds = [];
        if (user_store.supportAdmin) {
            profilesIds.push(...support_admin_user_profile_ids);
        }
        profilesIds.push(...admin_user_profile_ids);

        return [...new Set(profilesIds)];
    });

    const getInviteAssociateProfiles = computed(() => {
        let ids = [];
        const excludeIds = new Map();

        roles.value.forEach((role) => {
            if (role.name === ROLES.SUPPORT_ADMIN && !user_store.supportAdmin) {
                role.user_profiles?.forEach((profile) => {
                    if (profile?.role === ROLES.SUPPORT_ADMIN) {
                        excludeIds.set(profile.id, true);
                    }
                });
                return;
            }
            ids.push(...role.user_profile_ids);
        });

        const allIds = [
            ...ids,
            ...(inviterAdminPermission.value.user_profile_ids ?? []),
        ];

        return [...new Set([...allIds.filter((id) => !excludeIds.has(id))])];
    });

    function setTimezone(tz) {
        timezone.value = tz;
    }

    function getRoleByName(roleName) {
        return roles.value.find((role) => role.name === roleName);
    }

    async function fetchTimezoneOptions() {
        return manageAsyncOperation(async () => {
            const response = await axios.get("/timezones");
            const data = response.data.data ?? {};
            const keys = Object.keys(data);
            timezoneOptions.value = keys.map((key) => ({
                zone: key,
                data: data[key],
            }));
        });
    }

    async function fetchOrgTimezone() {
        return manageAsyncOperation(async () => {
            const response = await axios.get("/organizations/timezone");
            timezone.value = response.data?.data?.timezone;
        });
    }

    async function updateOrgTimezone(payload) {
        return manageAsyncOperation(async () => {
            await axios.post("/organizations/timezone", payload);
            timezone.value = payload.timezone;
        });
    }

    async function fetchPermissions() {
        return manageAsyncOperation(async () => {
            const { data } = await axios.get("/permissions");
            const permissions = data?.data ?? [];

            canStartWorkdayPermission.value = getPermission(
                permissions,
                PERMISSIONS.WORKDAY_START,
            );
            canStopWorkdayPermission.value = getPermission(
                permissions,
                PERMISSIONS.WORKDAY_STOP,
            );
            inviterAdminPermission.value = getPermission(
                permissions,
                PERMISSIONS.INVITER_ADMIN,
            );
        });
    }

    function getPermission(data, role) {
        return data.find((permission) => permission.name.includes(role)) ?? {};
    }

    async function fetchRoles() {
        return manageAsyncOperation(async () => {
            const { data } = await axios.get("/roles");
            roles.value = data.data;
        });
    }

    async function updateUserPemissions(userIds, permissionId) {
        return manageAsyncOperation(async () => {
            const response = await axios.post(`/permissions/${permissionId}`, {
                user_profile_ids: userIds,
            });
            return response;
        });
    }

    async function updateUserRoles(userIds, roleId) {
        return manageAsyncOperation(async () => {
            await axios.post(`/roles/${roleId}`, {
                user_profile_ids: userIds,
            });

            Utilities().showSuccessSnackbar(
                "Organization admins updated successfully",
            );
            eventBus.$emit("get-organization-roles");
        });
    }

    async function fetchOfficeHours() {
        return manageAsyncOperation(async () => {
            const { data } = await axios.get("organizations/office-hours");
            officeHours.value = data.data;
        });
    }

    async function updateOfficeHours(payload) {
        return manageAsyncOperation(async () => {
            await axios.post("organizations/office-hours", payload);
            Utilities().showSuccessSnackbar(
                "Work week hours updated successfully",
            );
        });
    }

    async function refetchRolePermissions() {
        fetchRoles();
        fetchPermissions();
    }

    return {
        timezone,
        timezoneOptions,
        isLoading,
        canStartWorkdayPermission,
        canStopWorkdayPermission,
        isWorkdayStartPermission,
        isWorkdayStopPermission,
        roles,
        hasWorkdayStartPermission,
        hasWorkdayStopPermission,
        fetchTimezoneOptions,
        fetchOrgTimezone,
        updateOrgTimezone,
        fetchPermissions,
        fetchRoles,
        setTimezone,
        inviterAdminPermission,
        ROLES,
        PERMISSIONS,
        getOwners,
        getAdmins,
        getInviteAssociateProfiles,
        updateUserPemissions,
        updateUserRoles,
        getRoleByName,
        fetchOfficeHours,
        officeHours,
        WORKDAYS,
        updateOfficeHours,
        refetchRolePermissions,
    };
});
